import { useState } from "react";
import { useHistory } from "react-router-dom";
import { UpdatePrivateSerieMutation } from "src/api/PrivateSerie";
import { UserQuery } from "src/api/User";
import BackButton from "src/components/buttons/BackButton";
import { BorderButton } from "src/components/buttons/BorderButton";
import InputError from "src/components/inputs/InputError";
import { InputText } from "src/components/inputs/InputText";
import { ConfirmModal } from "src/components/modals/ConfirmModal";
import MembershipModel, { isMembership } from "src/models/MembershipModel";
import PrivateSerieModel, {
  isPrivateSerie,
} from "src/models/PrivateSerieModel";
import UserModel from "src/models/UserModel";
import { apiClient } from "src/stores/Api";
import { useSession } from "src/stores/Session";
import { useStore } from "src/stores/Store";
import message from "src/utils/message";
import { modals, useModal } from "src/utils/modals";
import {
  EventType,
  Frequency,
  PrivateSerieStatus,
  updatePrivateSerie,
  updatePrivateSerieVariables,
} from "types/code-generator";
import { v4 as uuid } from "uuid";
import InputCheckbox from "../inputs/InputCheckbox";
import Modal, { ModalProps } from "./Modal";

interface Props extends ModalProps {
  registration: MembershipModel | PrivateSerieModel;
  onNetworkAction?: () => void;
}

export const ManageRosterModal = modals.create((props: Props) => {
  const modal = useModal();
  const store = useStore();
  const history = useHistory();
  const session = useSession();

  const [members, setMembers] = useState([
    ...(props.registration?.accountOwner?.students || []),
  ]);
  const [roster, setRoster] = useState([...props.registration.roster]);
  const [canceling, setCanceling] = useState(false);
  const [error, setError] = useState(null);

  async function cancelPrivateSerie() {
    if (!isPrivateSerie(props.registration)) {
      return;
    }

    const remainingLessons = props.registration.getRemainingLessons();

    modals.show(ConfirmModal, {
      title: `Cancel ${props.registration.offering.type}?`,
      body: (
        <>
          <p>
            This registration will change to Flexible Scheduling. Automatic
            billing and renewal will stop immediately.
          </p>
          <p>
            Tap Continue to manage any remaining sessions and review refund
            options.
          </p>
        </>
      ),
      buttonText: "Continue",
      handleOk: async () => {
        if (remainingLessons === 0) {
          const variables: updatePrivateSerieVariables = {
            data: {
              events: {
                create: [
                  {
                    type: EventType.Canceled,
                    by: {
                      connect: {
                        id: store.user.id,
                      },
                    },
                  },
                ],
              },
              offering: {
                update: {
                  frequency: Frequency.OneTime,
                },
              },
              status: PrivateSerieStatus.Canceled,
            },
            where: {
              id: props.registration.id,
            },
          };

          await apiClient<updatePrivateSerie>(UpdatePrivateSerieMutation, {
            variables,
          });

          await props.onNetworkAction();
        } else {
          history.push(
            `/admin/custom-private-lessons/${props.registration.id}/add?cancel=true`
          );
        }
      },
    });
  }

  async function onSubmit() {
    if (roster.length === 0) {
      if (isMembership(props.registration)) {
        setCanceling(true);
      } else if (isPrivateSerie(props.registration)) {
        cancelPrivateSerie();
        handleCancel();
      }

      return;
    }

    if (isMembership(props.registration)) {
      if (!props.registration.offering.allowSharing && roster.length > 1) {
        setError("This membership does not allow multiple participants");
        return;
      }

      const newMember = roster.find((r) => r.id.includes("-"));

      if (newMember || roster.length !== props.registration.roster.length) {
        const processingMessage = message.loading("Processing…");

        if (!store.isAdminArea) {
          // const variables: updateMembershipVariables = {
          //   data: {
          //     roster: {
          //       set: roster
          //         .filter((r) => !r.id.includes("-"))
          //         .map((r) => ({ id: r.id })),
          //     },
          //   },
          //   where: {
          //     id: props.registration.id,
          //   },
          // };

          session.clearSession();
          session.updateAccountOwner(new UserModel(store.user));
          session.setStudents([
            ...store.user.students,
            ...members.filter((m) => m.id.includes("-")),
          ]);

          session.addMembership(
            new MembershipModel({
              ...props.registration,
              roster,
            })
          );

          history.push(
            `/account/memberships/${props.registration.id}/checkout`
          );

          // await apiClient(UpdateMembershipMutation, {
          //   variables,
          // });

          // message.success("Your membership has been updated.");
        } else {
          const userQuery = await apiClient(UserQuery, {
            variables: {
              id: props.registration.accountOwner.id,
              organizationId: store.organization.id,
            },
          });
          const user = new UserModel(userQuery.user);

          session.clearSession();
          session.updateAccountOwner(new UserModel(user));
          session.setStudents([
            ...user.students,
            ...members.filter((m) => m.id.includes("-")),
          ]);

          session.addMembership(
            new MembershipModel({
              ...props.registration,
              roster,
            })
          );

          if (newMember) {
            history.push(
              `/admin/memberships/${props.registration.id}/participant-details/${newMember.id}`
            );
          } else {
            history.push(
              `/admin/memberships/${props.registration.id}/checkout`
            );
          }
        }

        processingMessage();
      }
    } else if (isPrivateSerie(props.registration)) {
      const processingMessage = message.loading("Processing…");

      const variables: updatePrivateSerieVariables = {
        data: {
          roster: {
            set: roster.map((r) => ({ id: r.id })),
          },
        },
        where: {
          id: props.registration.id,
        },
      };

      await apiClient<updatePrivateSerie, updatePrivateSerieVariables>(
        UpdatePrivateSerieMutation,
        {
          variables,
        }
      );

      processingMessage();

      if (props.onNetworkAction) {
        props.onNetworkAction();
      }
    }

    handleCancel();
  }

  function handleCancel() {
    if (props.handleCancel) {
      props.handleCancel();
    }

    modal.hide();
  }

  function afterClose() {
    modal.remove();
  }

  function addMember() {
    const currentMembers = [...members];
    const currentRoster = [...roster];

    const newMember = new UserModel({
      id: uuid(),
      firstName: `Member ${currentMembers.length + 1}`,
    });

    currentMembers.push(newMember);
    currentRoster.push(newMember);

    setMembers(currentMembers);
    setRoster(currentRoster);
  }

  function toggleMember(member) {
    let currentRoster = [...roster];

    if (currentRoster.find((r) => r.id === member.id)) {
      currentRoster = currentRoster.filter((c) => c.id !== member.id);
    } else {
      currentRoster.push(member);
    }

    setRoster(currentRoster);
  }

  function setMemberName(firstName, memberId) {
    const newMembers = [...members];
    const newMemberIndex = newMembers.findIndex((s) => s.id === memberId);
    newMembers[newMemberIndex].firstName = firstName;

    setMembers(newMembers);
  }

  let title = "Manage Members";
  let description = "Members for this registration";
  let cancelDescription =
    "There are no members selected. Do you want to cancel the registration?";

  if (isPrivateSerie(props.registration)) {
    title = "Manage Participants";
    description = "Participants for this registration";
    cancelDescription =
      "There are no participants selected. Do you want to cancel the registration?";
  }

  const cancelContent = (
    <>
      <div className="mb-4">
        <BackButton onClick={() => setCanceling(false)} backText="Previous" />
      </div>
      <div>{cancelDescription}</div>
    </>
  );

  const addMembersContent = (
    <>
      <div className="border-b border-borderGray pb-2">{description}</div>
      <div className="mb-4 divide-y divide-borderGray border-b border-borderGray">
        {members.map((s) => {
          if (s.id.includes("-")) {
            return (
              <InputCheckbox
                key={s.id}
                checked={Boolean(roster.find((r) => r.id === s.id))}
                className="items-center py-3"
                onChange={() => toggleMember(s)}
              >
                <div className="flex-1">
                  <div className="step-subhead relative z-10 -mb-1 pl-4 text-2xs">
                    First Name
                  </div>
                  <InputText
                    className="border-none bg-white"
                    placeholder={`Add Member`}
                    value={s.firstName}
                    onChange={(name) => setMemberName(name, s.id)}
                    selectTextOnFocus
                    autoFocus
                  />
                </div>
              </InputCheckbox>
            );
          }

          return (
            <InputCheckbox
              key={s.id}
              checked={Boolean(roster.find((r) => r.id === s.id))}
              className="items-center py-3"
              onChange={() => toggleMember(s)}
            >
              <div className="flex h-12 flex-1 items-center">
                <div className="flex-1">
                  <strong>
                    {s.firstName} {s.lastName}
                  </strong>
                </div>
              </div>
            </InputCheckbox>
          );
        })}
      </div>

      <div className="mb-2">
        <InputError error={error} />
      </div>

      {isMembership(props.registration) &&
        store.isAdminArea &&
        props.registration.offering.allowSharing && (
          <BorderButton
            dashed
            className="w-full"
            onClick={addMember}
            bgColor="#fff"
          >
            <div className="flex items-center">
              <i aria-hidden className="far fa-plus text-organization" />
              <div className="flex-1">Add another member</div>
            </div>
          </BorderButton>
        )}
    </>
  );

  return (
    <Modal
      visible={modal.visible}
      title={title}
      handleCancel={handleCancel}
      afterClose={afterClose}
      footer={
        <>
          {!canceling && (
            <>
              <BorderButton type="submit" onClick={onSubmit}>
                {isMembership(props.registration) ? "Next" : "Save"}
              </BorderButton>
              <BorderButton onClick={handleCancel}>Cancel</BorderButton>
            </>
          )}
          {canceling && (
            <>
              <BorderButton
                type="submit"
                onClick={() => {
                  if (isMembership(props.registration)) {
                    if (store.isAdminArea) {
                      history.push(
                        `/admin/memberships/${props.registration.id}/cancel`
                      );
                    } else {
                      history.push(
                        `/account/registrations/${props.registration.id}/cancel`
                      );
                    }
                  } else if (isPrivateSerie(props.registration)) {
                    history.push(
                      `/admin/custom-private-lessons/${props.registration.id}/cancel`
                    );
                  }

                  handleCancel();
                }}
              >
                Yes, Cancel
              </BorderButton>
              <BorderButton onClick={() => setCanceling(false)}>
                Go Back
              </BorderButton>
            </>
          )}
        </>
      }
    >
      {canceling && cancelContent}
      {!canceling && addMembersContent}
    </Modal>
  );
});
